import type { TooltipContentProps } from "recharts";
import * as React from "react";
import type { ChartTooltipProps } from "@wayflyer/flyui-dataviz";
import {
  BarBlock,
  LongDash,
  LongDashedDash,
  TooltipContainer,
  TooltipContent,
  TooltipDash,
  TooltipLine,
  TooltipMain,
  TooltipPercentChangeText,
  TooltipTitle,
} from "@wayflyer/flyui-dataviz";

import type { ValueType } from "../shared";

export const profitabilityTooltip =
  (formatValue: (rawValue: ValueType, isROAS?: boolean) => string | number, periodAbrv: string) =>
  ({ active, payload, label }: TooltipContentProps) => {
    return getProfitabilityTooltip({ active, payload, label, formatValue, periodAbrv });
  };

const getPercentMovement = (currentVal: number, previousValue: number) => {
  const mult = currentVal < previousValue ? -1 : 1;
  const movement = mult * 100 * Math.abs((previousValue - currentVal) / previousValue);
  return movement.toFixed(1);
};

const getProfitabilityTooltip = ({ active, payload, label, formatValue, periodAbrv }: ChartTooltipProps) => {
  if (!active) {
    return null;
  }

  const includesLastYearsValue = payload?.find(
    (payloadEntry) => payloadEntry.dataKey?.toString().substr(0, 9) === "lastYears"
  );

  const continuationString = "ValueContinuation";
  const legendItems = payload?.map((payloadEntry) => {
    const dataKey = payloadEntry.dataKey?.toString() || "";
    if (
      (includesLastYearsValue &&
        dataKey.substring(dataKey.length - continuationString.length) === continuationString) ||
      dataKey.indexOf("PreviousValue") >= 0
    ) {
      return null;
    }

    const isRAOS = (payloadEntry.dataKey?.toString() || "").indexOf("ROAS") >= 0;
    const isLastYearsProfit = (payloadEntry.dataKey?.toString() || "").indexOf("lastYearsProfit") >= 0;
    const isLastYear = (payloadEntry.dataKey?.toString() || "").indexOf("lastYear") >= 0;
    const isProfit = (payloadEntry.dataKey?.toString() || "").indexOf("YearsProfit") >= 0;
    const previousValueDataKey = payloadEntry?.dataKey?.toString().replace("Value", "PreviousValue") || "";

    const previousValue = payloadEntry?.payload[previousValueDataKey]
      ? +payloadEntry?.payload[previousValueDataKey]
      : null;
    const currentValue = +(payloadEntry?.value || "");

    const percentChange =
      !isLastYear && previousValue !== null ? getPercentMovement(currentValue, previousValue) + `% ${periodAbrv}` : "";
    return (
      <TooltipLine key={String(payloadEntry.dataKey)} style={{ color: payloadEntry.color }}>
        <TooltipDash>
          {isLastYear && !isLastYearsProfit ? (
            <LongDashedDash color={payloadEntry.color || "white"} />
          ) : isProfit ? (
            <BarBlock color={payloadEntry.color || "white"} dashed={isLastYearsProfit} barOpacity={-1} />
          ) : (
            <LongDash color={payloadEntry.color || "white"} />
          )}
        </TooltipDash>
        {payloadEntry.value !== undefined ? `${payloadEntry.name}: ${formatValue(payloadEntry.value, isRAOS)} ` : null}
        {percentChange ? <TooltipPercentChangeText>({percentChange})</TooltipPercentChangeText> : null}
      </TooltipLine>
    );
  });

  return (
    <TooltipContainer>
      <TooltipMain>
        <TooltipTitle>{label}</TooltipTitle>
        {legendItems?.length ? <TooltipContent>{legendItems}</TooltipContent> : null}
      </TooltipMain>
    </TooltipContainer>
  );
};
